import { useCallback, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FastField, Form, Formik, Field } from 'formik';

import { PAYMENT_METHODS } from 'bxcommon/constants/payments.constant';
import { RequestContext } from 'bxcommon/context/Request.context';
import { updateElectronicDelivery } from 'bxcommon/helpers/common.helpers';
import { ModalContext } from 'bxcommon/context/Modal.context';
import { isFormEditable } from 'bxcommon/helpers/form.helper';
import {
  BaseBox,
  BaseBoxHeading,
  BaseHint,
  BaseTextArea,
  BaseButtonOutline,
  BaseButtonFill,
  BaseRadio,
  InputField,
  CheckboxField,
} from 'bxcommon';
import { updateRequestField } from '../../store/slices/request.slice';
import { useUpdateAdditionalRecipientsMutation } from '../../store/endpoints/registry.endpoints';
import { successToast, errorToast } from 'bxcommon/store/slices/toaster.slice';

import { isPaymentProceededSuccessfully } from '../../common/common.helpers';
import { additionalRecipientsValidationSchema } from '../RequestForm/RequestFormValidation';

import './Confirmations.scss';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const Confirmations = (props) => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const dispatch = useDispatch();
  const [updateAdditionalRecipients] = useUpdateAdditionalRecipientsMutation();

  const {
    request,
    formRef,
    resources: { payment_methods },
  } = useContext(RequestContext);

  const { openConfirmModal } = useContext(ModalContext);

  const { validationSchema, isRenewal, isEmployerChange } = props;

  const regularPayment = request.payment ? request.payment.status : null;
  const amendmentPayment = request.amendment && request.amendment.payment ? request.amendment.payment.status : null;
  const isExpressPaymentRequired = Boolean(request.express_payment_needed);

  const initialConfirmations = {
    not_been_convicted: false,
    no_criminal_record: false,
    no_under_prohibition: false,
    not_registered_in_other_office: false,
    all_information_correct: false,
    aware_of_obligations: false,
    electronic_delivery_allowed: true,
  };

  const renewalConfirmations = {
    not_been_convicted: false,
    no_criminal_record: false,
    no_under_prohibition: false,
    all_information_correct: false,
    aware_of_obligations: false,
    electronic_delivery_allowed: true,
  };

  const employerChangeConfirmations = {
    not_registered_in_other_office: false,
    all_information_correct: false,
    aware_of_obligations: false,
  };

  const getSessionStorageEmail = JSON.parse(sessionStorage.getItem('regsevices-form-values-additional-email')) || '';
  const getSessionStorageFirstName = JSON.parse(sessionStorage.getItem('regsevices-form-values-firstname'));
  const getSessionStorageLastName = JSON.parse(sessionStorage.getItem('regsevices-form-values-lastname'));

  const initialConfirmationsChecks = [
    {
      name: 'confirmations.not_been_convicted',
      confirmationKey: 'not_been_convicted',
      text: t('request.summary.confirmations.notBeenConvicted'),
    },
    {
      name: 'confirmations.no_criminal_record',
      confirmationKey: 'no_criminal_record',
      text: t('request.summary.confirmations.noCriminalRecord'),
    },
    {
      name: 'confirmations.no_under_prohibition',
      confirmationKey: 'no_under_prohibition',
      text: t('request.summary.confirmations.noUnderProhibition'),
    },
    {
      name: 'confirmations.not_registered_in_other_office',
      confirmationKey: 'not_registered_in_other_office',
      text: t('request.summary.confirmations.notRegisteredInOtherOffice'),
    },
    {
      name: 'confirmations.aware_of_obligations',
      confirmationKey: 'aware_of_obligations',
      text: t('request.summary.confirmations.awareOfObligations'),
    },
    {
      name: 'confirmations.all_information_correct',
      confirmationKey: 'all_information_correct',
      text: t('request.summary.confirmations.allInformationCorrect'),
    },
  ];

  const renewalConfirmationsChecks = [
    {
      name: 'confirmations.not_been_convicted',
      confirmationKey: 'not_been_convicted',
      text: t('request.summary.confirmations.notBeenConvicted'),
    },
    {
      name: 'confirmations.no_criminal_record',
      confirmationKey: 'no_criminal_record',
      text: t('request.summary.confirmations.noCriminalRecord'),
    },
    {
      name: 'confirmations.no_under_prohibition',
      confirmationKey: 'no_under_prohibition',
      text: t('request.summary.confirmations.noUnderProhibition'),
    },
    {
      name: 'confirmations.aware_of_obligations',
      confirmationKey: 'aware_of_obligations',
      text: t('request.summary.confirmations.awareOfObligations'),
    },
    {
      name: 'confirmations.all_information_correct',
      confirmationKey: 'all_information_correct',
      text: t('request.summary.confirmations.allInformationCorrect'),
    },
  ];

  const employerChangeConfirmationsChecks = [
    {
      name: 'confirmations.not_registered_in_other_office',
      confirmationKey: 'not_registered_in_other_office',
      text: t('request.summary.confirmations.notRegisteredInOtherOffice'),
    },
    {
      name: 'confirmations.aware_of_obligations',
      confirmationKey: 'aware_of_obligations',
      text: t('request.summary.confirmations.awareOfObligations'),
    },
    {
      name: 'confirmations.all_information_correct',
      confirmationKey: 'all_information_correct',
      text: t('request.summary.confirmations.allInformationCorrect'),
    },
  ];

  const confirmationChecks = isRenewal
    ? renewalConfirmationsChecks
    : isEmployerChange
      ? employerChangeConfirmationsChecks
      : initialConfirmationsChecks;

  const initialValues = {
    confirmations: isRenewal
      ? renewalConfirmations
      : isEmployerChange
        ? employerChangeConfirmations
        : initialConfirmations,
    comment: '',
    approvals: {
      terms_of_use: false,
      rules_of_registration_office: false,
      list_of_fees: false,
    },
    additional_recipients:
      isRenewal && getSessionStorageEmail ? getSessionStorageEmail : request.additional_recipients || '',
    payment: {
      method: 1,
      voucher_code: '',
      express_process: isRenewal ? isExpressPaymentRequired : false,
      accepted_additional_fee_agreement: false,
      status: request.amendment ? amendmentPayment : regularPayment,
      express_payment_needed: isExpressPaymentRequired,
    },
  };

  const electronicDeliveryModal = (e) => {
    let result = updateElectronicDelivery(openConfirmModal, updateRequestField, t, { target: { checked: !e } });
    if (result === false) {
      formRef.current.setFieldValue('confirmations.electronic_delivery_allowed', false, false);
    }
  };

  const uploadAdditionalEmailSessionStorage = (additional_recipients) => {
    sessionStorage.setItem('regsevices-form-values-additional-email', JSON.stringify(additional_recipients));
  };

  const uploadExpressAdditionalFeeAgreement = (value) => {
    sessionStorage.setItem('regservices-form-values-accepted_additional_fee_agreement', JSON.stringify(value));
  };

  const uploadExpressProcess = useCallback((value) => {
    sessionStorage.setItem('regservices-form-values-express_process', JSON.stringify(value));
    uploadExpressAdditionalFeeAgreement(false);
  }, []);

  useEffect(() => {
    uploadExpressProcess(false);
  }, [uploadExpressProcess]);

  useEffect(() => {
    const form = formRef.current;
    if (!getSessionStorageEmail && request.additional_recipients) {
      uploadAdditionalEmailSessionStorage(request.additional_recipients);
    }

    return () => {
      uploadAdditionalEmailSessionStorage(form.values.additional_recipients);
    };
  }, [formRef, getSessionStorageEmail, request.additional_recipients]);

  const isChecked = (values, type) => values.payment.method && values.payment.method === type;

  const createRadio = (type, values, disabled = false) => {
    const item = payment_methods.data.find((i) => i.id === type);

    return (
      <Field
        component={BaseRadio}
        checked={isChecked(values, type)}
        name="payment.method"
        disabled={disabled}
        value={item.id}
        data-testid="type-of-payment"
      >
        {item.name}
      </Field>
    );
  };

  const [showAdditionalRecipientsEditionBox, setShowAdditionalRecipientsEditionBoxFlag] = useState(false);
  const submitAdditionalRecipientsChange = async ({ additional_recipients }) => {
    try {
      await updateAdditionalRecipients({
        new_additional_recipients: additional_recipients,
        case_id: request.case_id,
      }).unwrap();
      successToast(common('request.details.additionalRecipientEmails.successMessage'), common('successToastHeading'));
      setShowAdditionalRecipientsEditionBoxFlag(false);
    } catch (_) {
      errorToast(common('request.details.additionalRecipientEmails.errorMessage'), common('errorToastHeading'));
    }
  };

  const feeLink = (
    <a href="https://www.regservices.ch/?download=25337" rel="noopener noreferrer" target="_blank" className="fee-link">
      List of Fees
    </a>
  );

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formRef}
      onSubmit={async (values) => {
        await dispatch(updateRequestField({ ...values }));
        await sleep(300); // TODO: check if this is needed after the thunk updateRequestField is done
      }}
      validationSchema={validationSchema}
      className="confirmations-form"
    >
      {({ values }) => (
        <div className="confirmations">
          {/* confirmations list of checkboxes */}
          {(isFormEditable(request.status) || isRenewal || isEmployerChange) && (
            <BaseBox borderless className="base-summary__comment" data-testid="summary-confirmations">
              <BaseBoxHeading>{t('request.summary.headings.confirmations')}</BaseBoxHeading>
              <div>
                <h4 className="heading heading--extra-light">
                  {t('request.summary.headings.self')},{' '}
                  <b>
                    {isRenewal ? getSessionStorageFirstName : request.first_name}{' '}
                    {isRenewal ? getSessionStorageLastName : request.last_name}
                  </b>{' '}
                  {t('request.summary.headings.confirmation')}
                </h4>
              </div>
              <div className="row request-details--column">
                <div className="col-12">
                  {confirmationChecks.map((check, index) => (
                    <CheckboxField
                      key={index}
                      name={check.name}
                      checked={values.confirmations[check.confirmationKey]}
                      text={check.text}
                    />
                  ))}
                  {!isEmployerChange && (
                    <CheckboxField
                      name="confirmations.electronic_delivery_allowed"
                      checked={values.confirmations.electronic_delivery_allowed}
                      onChange={electronicDeliveryModal}
                      text={t('request.summary.confirmations.electronicDeliveryAllowed')}
                    />
                  )}
                </div>
                {!isEmployerChange && (
                  <div className="col-12">
                    <p className="copy copy--small request-details__fees">
                      <Trans i18nKey="request.summary.additionalFees">
                        <a
                          href="https://www.post.ch/en/business/a-z-of-subjects/incamail"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          IncaMail
                        </a>
                      </Trans>
                    </p>
                  </div>
                )}
              </div>
            </BaseBox>
          )}

          {/* additional recipients of decision */}
          <BaseBox borderless className="base-summary__comment mt-16">
            <BaseBoxHeading>{t('request.summary.additionalRecipients')}</BaseBoxHeading>
            {isFormEditable(request.status) || isRenewal || isEmployerChange ? (
              <Form className="form-container request-details__additional-recipients">
                <div className="row request-form--with-hint">
                  <div className="col-12">
                    <h4 className="heading heading--extra-light">{t('request.summary.additionalRecipientsLabel')}</h4>
                    <InputField name="additional_recipients" label={null} />
                  </div>
                  <BaseHint className="base-hint--for-full-width-form-box">
                    {t('request.form.hints.additionalRecipients')}
                  </BaseHint>
                </div>
              </Form>
            ) : (
              <div>
                {showAdditionalRecipientsEditionBox ? (
                  <Formik
                    initialValues={{
                      additional_recipients: request.additional_recipients,
                    }}
                    onSubmit={submitAdditionalRecipientsChange}
                    validationSchema={additionalRecipientsValidationSchema(true)}
                    validateOnChange={true}
                  >
                    {({ handleSubmit }) => (
                      <form onSubmit={handleSubmit} className="change-recipients-form">
                        <InputField name="additional_recipients" label={null} />
                        <div className="change-recipients-form__buttons">
                          <BaseButtonOutline
                            type="button"
                            small
                            onClick={() => setShowAdditionalRecipientsEditionBoxFlag(false)}
                          >
                            {common('request.details.additionalRecipientEmails.cancelBtn')}
                          </BaseButtonOutline>
                          <BaseButtonFill type="submit" small className="ml-16">
                            {common('request.details.additionalRecipientEmails.submitBtn')}
                          </BaseButtonFill>
                        </div>
                      </form>
                    )}
                  </Formik>
                ) : (
                  <div>
                    <div>{request.additional_recipients || t('request.summary.noAdditionalRecipients')}</div>
                    {!showAdditionalRecipientsEditionBox && (
                      <div className="additional-recipients-change-button mt-16">
                        <BaseButtonOutline
                          onClick={() => setShowAdditionalRecipientsEditionBoxFlag(true)}
                          small
                          iconRight
                          icon="pencil"
                          data-testid="details-request-change-button"
                        >
                          {common('request.details.changeAdditionalRecipientEmails')}
                        </BaseButtonOutline>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </BaseBox>

          {/* urgent applications for additional fee information */}
          {(!(isRenewal || isEmployerChange) || isExpressPaymentRequired) && (
            <BaseBox borderless className="base-summary__section mt-16" data-testid="urgent-applications">
              <BaseBoxHeading>{t('request.summary.headings.urgentApplications')}</BaseBoxHeading>
              {isRenewal && isExpressPaymentRequired && (
                <div
                  onChange={() => {
                    if (!values.payment.accepted_additional_fee_agreement) {
                      values.payment.method = PAYMENT_METHODS.CREDIT_CARD;
                    }
                    values.payment.express_process = true;
                  }}
                >
                  <BaseBox box className="base-summary__section code-of-conduct renewal-fee-agreement">
                    <Trans i18nKey="request.summary.estimationExpress.renewalAdditionalFeeAgreement">{feeLink}</Trans>
                  </BaseBox>
                  <CheckboxField
                    name="payment.accepted_additional_fee_agreement"
                    checked={values.payment.accepted_additional_fee_agreement}
                    onChange={uploadExpressAdditionalFeeAgreement}
                    text={t('request.summary.estimationExpress.renewalAdditionalFee')}
                  />
                </div>
              )}
              {!isRenewal && !isEmployerChange && (
                <div
                  className="row request-form--with-hint request-details--column"
                  onChange={() => {
                    if (!values.payment.express_process) {
                      values.payment.method = PAYMENT_METHODS.CREDIT_CARD;
                    }
                    values.payment.accepted_additional_fee_agreement = false;
                  }}
                >
                  <div>
                    <CheckboxField
                      name="payment.express_process"
                      checked={values.payment.express_process}
                      onChange={uploadExpressProcess}
                      text={<Trans i18nKey="request.summary.estimationExpress.additionalFeeAgreement">{feeLink}</Trans>}
                    />
                  </div>
                  {values.payment.express_process && (
                    <div className="additional-fee-checkbox">
                      <CheckboxField
                        name="payment.accepted_additional_fee_agreement"
                        checked={values.payment.accepted_additional_fee_agreement}
                        onChange={uploadExpressAdditionalFeeAgreement}
                        text={t('request.summary.estimationExpress.urgentApplicationAgreement')}
                      />
                    </div>
                  )}
                </div>
              )}
            </BaseBox>
          )}

          {/* payments/billing information */}

          <BaseBox borderless className="base-summary__section mt-16" data-testid="payment">
            <BaseBoxHeading>{t('request.summary.headings.paymentMethod')}</BaseBoxHeading>
            {!isPaymentProceededSuccessfully(values.payment) ||
            !isPaymentProceededSuccessfully(values.amendment.payment) ? (
              <div
                className="row request-form--with-hint request-details--column"
                onChange={() => {
                  values.payment.voucher_code = '';
                }}
              >
                <div>{t('request.summary.paymentBox')}</div>
                <div className="radio-group">
                  <label>{createRadio(PAYMENT_METHODS.CREDIT_CARD, values)}</label>
                  <label>
                    {createRadio(
                      PAYMENT_METHODS.VOUCHER_CODE,
                      values,
                      values.payment.express_process || isExpressPaymentRequired
                    )}
                  </label>
                </div>
                {values.payment.method === PAYMENT_METHODS.VOUCHER_CODE && (
                  <div>
                    <InputField name="payment.voucher_code" label={null} />
                  </div>
                )}
                <BaseHint className="base-hint--for-full-width-form-box">
                  {t('request.form.hints.paymentMethod')}
                </BaseHint>
              </div>
            ) : (
              <>
                <h4 className="heading heading--extra-light">{t('request.summary.paymentAlreadyMade')}</h4>
              </>
            )}
          </BaseBox>

          {/* comments */}

          <BaseBox borderless className="base-summary__comment mt-16" data-testid="summary-comment">
            <BaseBoxHeading>
              {t('request.summary.headings.comments')}{' '}
              {(isFormEditable(request.status) || isRenewal || isEmployerChange) &&
                common('request.form.common.optional')}
            </BaseBoxHeading>
            {isFormEditable(request.status) || isRenewal || isEmployerChange ? (
              <div>
                <div>{common('request.summary.commentBox')}</div>
                <FastField
                  component={BaseTextArea}
                  name="comment"
                  value={values.comment}
                  data-testid="additional_certificates"
                />
              </div>
            ) : (
              <h4 className="heading heading--extra-light">{values.comment}</h4>
            )}
          </BaseBox>

          {/* accept terms */}

          {(isFormEditable(request.status) || isRenewal || isEmployerChange) && (
            <BaseBox borderless className="base-summary__comment mt-16" data-testid="summary-approvals">
              <BaseBoxHeading>{t('request.summary.accept')}</BaseBoxHeading>
              <div className="row">
                <div className="col-12">
                  <CheckboxField
                    name="approvals.terms_of_use"
                    checked={values.approvals.terms_of_use}
                    text={
                      <div>
                        <a
                          href="https://www.regservices.ch/en/terms-of-use/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          {t('request.summary.approvals.termsOfUse')}
                        </a>
                      </div>
                    }
                  />
                  <CheckboxField
                    name="approvals.rules_of_registration_office"
                    checked={values.approvals.rules_of_registration_office}
                    text={
                      <div>
                        <a
                          href="https://www.regservices.ch/en/regulations-registrationoffice/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          {t('request.summary.approvals.rulesOfRegistrationOffice')}
                        </a>
                      </div>
                    }
                  />
                  <CheckboxField
                    name="approvals.list_of_fees"
                    checked={values.approvals.list_of_fees}
                    text={
                      <div>
                        <a
                          href="https://www.regservices.ch/en/regulations-registrationoffice/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link--underlined"
                        >
                          {t('request.summary.approvals.listOfFees')}
                        </a>
                      </div>
                    }
                  />
                </div>
              </div>
            </BaseBox>
          )}
        </div>
      )}
    </Formik>
  );
};

export default Confirmations;
